import { ConsumerProfile, ConsumerState } from '../../state'
import { TFunction } from 'next-i18next'
import { DropdownItems, FieldType, Schema, SchemaConstraint } from '@afterpaytouch/form'
import { dobValidation, expiryDateValidation, nameMaxLengthValidation, namePattern, requiredValidation } from '../VerifyIdentity/validations'
import { IdentityPassportCountries } from '@afterpaytouch/portal-api/identity/types'
import { PassportFormData } from './Passport'
import { InputOnFocus } from '@afterpaytouch/core'
import { getDateSchema, getDropdownFieldSchema, getInputBirthdaySchema, getInputFieldSchema } from '../VerifyIdentity/utils'
import { RegisterOptions } from 'react-hook-form'
import { Country } from '@afterpaytouch/portal-api'
import { COUNTRY_FIELDS_HIDDEN } from './constants'
import { passportPattern } from './validations'

export interface GetSchemaAndFormDataResult<T> {
  schema: Schema<SchemaConstraint<T>>
  initialFormData: T
}

const setHiddenFieldsByCountry = (schema: Schema<PassportFormData>, country?: Country): void => {
  const hiddenFields = COUNTRY_FIELDS_HIDDEN[country as Country] ?? []
  Object.entries(schema).forEach(([fieldName]) => {
    const isHidden: boolean = hiddenFields.includes(fieldName)

    if (isHidden) {
      schema[fieldName].show = false
    }
  })
}

const getFormData = (profile: ConsumerProfile | undefined): PassportFormData => {
  return {
    givenNames: profile?.givenNames ?? '',
    otherGivenNames: profile?.otherGivenNames ?? '',
    surname: profile?.surname ?? '',
    nameAlert: undefined,
    dateOfBirth: profile?.dateOfBirth ?? '',
    countryOfIssue: '',
    passportNumber: '',
    expiryDate: '',
    terms: undefined,
    footer: undefined,
  }
}

export const transformCountries = (permittedCountries?: IdentityPassportCountries): DropdownItems[] | null => {
  if (typeof permittedCountries === 'undefined' || permittedCountries === null) {
    return null
  }
  return Object.entries(permittedCountries)?.map(([key, value]) => ({
    key,
    label: value,
  }))
}

export interface SchemaUpdates {
  passportOptions: RegisterOptions
  showProfileDobAlert: boolean
  showProfileNameAlert: boolean
  permittedCountries?: IdentityPassportCountries
}

interface PassportComponents {
  nameAlert: () => JSX.Element
  dobAlert: () => JSX.Element
  profileCard: () => JSX.Element
  terms: () => JSX.Element
  footer: () => JSX.Element
}

interface PassportSchemaOptions {
  consumer: ConsumerState
  permittedCountries?: IdentityPassportCountries
  schemaUpdates: SchemaUpdates
  components: PassportComponents
  t: TFunction
  onBlur: (e: InputOnFocus, v: unknown) => void
  isProfileEditable: boolean
  error: boolean
}

export const getFormSchema = (opts: PassportSchemaOptions): GetSchemaAndFormDataResult<PassportFormData> => {
  const { consumer, permittedCountries, components, t, onBlur, schemaUpdates, error, isProfileEditable } = opts
  const { accountCountry, profile, preferredLocale } = consumer
  const { showProfileDobAlert, showProfileNameAlert, passportOptions } = schemaUpdates
  const initialFormData = getFormData(profile)
  const { givenNames, otherGivenNames, surname, dateOfBirth } = initialFormData
  const required = requiredValidation(t)
  const namePatterns = namePattern(t)
  const dobValidateOptions = dobValidation(t)
  const nameMaxLengthOptions = nameMaxLengthValidation(t)
  const expiryOptions = expiryDateValidation()

  const schema: Schema<PassportFormData> = {
    givenNames: getInputFieldSchema({
      label: `${t('verifyIdentity:passport:givenNames')}`,
      value: givenNames,
      inline: true,
      options: {
        ...required('givenNames'),
        ...namePatterns('givenNames'),
        ...nameMaxLengthOptions('givenNames', accountCountry),
        valueAsDate: false,
        valueAsNumber: false,
      },
      otherProps: {
        onBlur,
        'data-dd-action-name': 'Passport Given Name Input',
      },
      error,
      show: isProfileEditable,
    }),
    otherGivenNames: getInputFieldSchema({
      label: `${t('verifyIdentity:passport:otherGivenNames')}`,
      value: otherGivenNames,
      inline: true,
      options: {
        ...namePatterns('otherGivenNames'),
        ...nameMaxLengthOptions('otherGivenNames', accountCountry),
        valueAsDate: false,
        valueAsNumber: false,
      },
      otherProps: {
        onBlur,
        'data-dd-action-name': 'Passport Other Given Name Input',
      },
      error,
      show: isProfileEditable,
    }),
    surname: getInputFieldSchema({
      label: `${t('verifyIdentity:passport:surname')}`,
      value: surname,
      options: {
        ...required('surname'),
        ...namePatterns('surname'),
        ...nameMaxLengthOptions('surname', accountCountry),
        valueAsDate: false,
        valueAsNumber: false,
      },
      otherProps: {
        onBlur,
        'data-dd-action-name': 'Passport Surname Input',
      },
      error,
      show: isProfileEditable,
    }),
    nameAlert: {
      type: FieldType.Component,
      show: showProfileNameAlert,
      Component: components.nameAlert,
    },
    dateOfBirth: getInputBirthdaySchema({
      label: `${t('verifyIdentity:passport:dateOfBirth')}`,
      value: dateOfBirth,
      options: { ...required('dateOfBirth') },
      otherProps: {
        'data-dd-action-name': 'Passport Date Of Birth Input',
      },
      otherValidations: dobValidateOptions('dateOfBirth'),
      locale: preferredLocale,
      error,
      show: isProfileEditable,
    }),
    dobAlert: {
      type: FieldType.Component,
      show: showProfileDobAlert,
      Component: components.dobAlert,
    },
    countryOfIssue: getDropdownFieldSchema({
      label: `${t('verifyIdentity:passport:countryOfIssue')}`,
      options: { ...required('countryOfIssue') },
      otherProps: {
        'data-dd-action-name': 'Passport Country Of Issue Dropdown',
      },
      items: transformCountries(permittedCountries),
      error,
      show: isProfileEditable,
    }),
    profileCard: {
      type: FieldType.Component,
      Component: components.profileCard,
      show: !isProfileEditable,
    },
    passportNumber: getInputFieldSchema({
      label: `${t('verifyIdentity:passport:number')}`,
      options: {
        ...required('passportNumber'),
        ...passportOptions,
        ...(accountCountry === Country.NZ && passportPattern({ countryOfIssue: Country.NZ, t })),
        valueAsDate: false,
        valueAsNumber: false,
      },
      otherProps: {
        'data-dd-action-name': 'Passport Number Input',
      },
      error,
    }),
    expiryDate: getDateSchema({
      label: `${t('verifyIdentity:passport:expiryDate')}`,
      format: 'DD/MM/YYYY',
      options: {
        ...required('passportExpiryDate'),
        ...expiryOptions(t('verifyIdentity:error:invalid', { context: 'passportExpiryDate' })),
      },
      otherProps: {
        'data-dd-action-name': 'Passport Expiry Date Input',
      },
      show: consumer.accountCountry === Country.NZ,
    }),
    terms: {
      type: FieldType.Component,
      Component: components.terms,
    },
    footer: {
      type: FieldType.Component,
      Component: components.footer,
    },
  }

  setHiddenFieldsByCountry(schema, accountCountry)

  return { schema, initialFormData }
}
